/**
 * `LocalSaveData` — the two switches the settings panel flips, the ladder
 * position, and where they are kept.
 *
 * Decomp read for this file:
 *   bustraffic_kit/code/decomp_game/LocalSaveData$$.ctor__0x20da2fc.c
 *   bustraffic_kit/code/decomp_game/SoundService$$ToggleSound__0x20dc600.c
 *   bustraffic_kit/code/decomp_game/SoundService$$ToggleHaptic__0x20dc988.c
 *   bustraffic_kit/code/decomp_game/SoundService$$InitializeSettings__0x20db93c.c
 *   bustraffic_kit/code/decomp_game/LevelService$$.ctor__0x20d5ccc.c
 *   bustraffic_kit/code/decomp_game/LevelService$$OnLevelCompleted__0x20d7f70.c
 *
 *     class LocalSaveData { bool isSoundEnabled 0x10; bool isHapticEnabled 0x11;
 *                           int displayLevel 0x14; int attemptCount 0x18; }
 *     LocalSaveData..ctor:  *(short*)(this + 0x10) = 0x0101;  *(int*)(this + 0x14) = 1;
 *
 * 🔍 so a fresh save has **both switches on** and sits on ladder step 1.
 * `SoundService.InitializeSettings` copies the two flags out of
 * `SaveService.Data`, `ToggleSound` / `ToggleHaptic` flip the service's flag,
 * write it back into `Data`, and call `SaveService.SaveLocalAsync`.
 *
 * `SaveService` writes an (optionally obfuscated) file under
 * `_localFilePath`; the port keeps the same fields in `localStorage`. The key
 * is the port's own — `SaveService { bool _obfuscateData 0x10; string
 * _localFilePath 0x18 }` takes both from ctor arguments that IL2CPP turned into
 * `DAT_*`, so neither the file name nor the obfuscation flag is readable.
 *
 * ⚠️ This header used to end "`displayLevel` and `attemptCount` are the level
 * ladder's, which the port drives from the URL, so they are not kept here."
 * That was wrong about `displayLevel`, and WEB-4 reverses it: `LevelService`
 * has no URL, it **loads** `_userLevel` straight out of this field
 * (`LevelService..ctor`:30, `*(int*)(this+0x10) = *(int*)(Data+0x14)`) and
 * writes it back on every win. Driving it from the URL alone meant a fresh tab
 * always restarted at level 1 — progress the original keeps.
 *
 * ❓ PLACEHOLDER — `attemptCount` (0x18) is still not ported. `grep -n
 * attemptCount bustraffic_kit/code/dump.cs` returns exactly one line, the
 * declaration at 707735: nothing reads it and nothing writes it. §8 warns that
 * "no call site" is not proof of dead code, but here **both** directions are
 * empty, so porting it would mean inventing a meaning. To settle it: a build
 * where the field is touched, or the analytics schema that consumes it.
 */

export interface LocalSaveData {
  isSoundEnabled: boolean
  isHapticEnabled: boolean
  /**
   * 🔍 `LevelService._userLevel` (0x10) is a copy of this. It is a **ladder
   * step**, not a file number — `GetResolvedFileIndex` maps it onto the files
   * that exist, and past the last one it loops back to 16 rather than to 1.
   */
  displayLevel: number
}

/** 🔍 `LocalSaveData..ctor` — `0x0101` into 0x10, then `1` into 0x14. */
export const DEFAULT_LOCAL_SAVE: Readonly<LocalSaveData> = {
  isSoundEnabled: true,
  isHapticEnabled: true,
  displayLevel: 1,
}

export const LOCAL_SAVE_KEY = 'bus-traffic-loop.localSave'

/** The slice of `Storage` used, so a test can pass a Map-backed stub. */
export interface SaveStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export class LocalSave {
  readonly data: LocalSaveData

  /**
   * Whether this device already had a save when the page opened.
   *
   * Not a detail of the read: it is what tells a reinstall (no file, so the
   * defaults carry no player intent) apart from an ordinary session (a file,
   * so "sound off" is a choice). The cloud merge is the only caller and it
   * needs exactly that distinction — see `monetization/cloudSave.ts`.
   */
  readonly hasStoredFile: boolean

  /**
   * Called after every write. ❓ Not in the game — `SaveService.SaveLocalAsync`
   * has one destination; the wrapper app adds a second (`save_user_data`), and
   * this is where it hangs so that no call site has to remember both.
   */
  onSaved: ((data: LocalSaveData) => void) | null = null

  constructor(
    private readonly storage: SaveStorage | null,
    private readonly key = LOCAL_SAVE_KEY,
  ) {
    this.hasStoredFile = LocalSave.exists(storage, key)
    this.data = { ...DEFAULT_LOCAL_SAVE, ...LocalSave.read(storage, key) }
  }

  private static exists(storage: SaveStorage | null, key: string): boolean {
    if (!storage) return false
    try {
      const raw = storage.getItem(key)
      return raw !== null && raw !== ''
    } catch {
      return false
    }
  }

  /**
   * Take the cloud merge's answer. Writes and saves like any other mutation, so
   * the merged state is on this device before the first level loads — a player
   * who never finishes another level still keeps the restored ladder step.
   */
  applyCloud(next: LocalSaveData): void {
    this.data.isSoundEnabled = next.isSoundEnabled
    this.data.isHapticEnabled = next.isHapticEnabled
    this.data.displayLevel = next.displayLevel
    this.save()
  }

  /** `SaveService.InitializeAsync` — a missing or unreadable file is a new save. */
  private static read(storage: SaveStorage | null, key: string): Partial<LocalSaveData> {
    if (!storage) return {}
    try {
      const raw = storage.getItem(key)
      if (!raw) return {}
      const parsed: unknown = JSON.parse(raw)
      if (typeof parsed !== 'object' || parsed === null) return {}
      const out: Partial<LocalSaveData> = {}
      const p = parsed as Record<string, unknown>
      if (typeof p.isSoundEnabled === 'boolean') out.isSoundEnabled = p.isSoundEnabled
      if (typeof p.isHapticEnabled === 'boolean') out.isHapticEnabled = p.isHapticEnabled
      // A stored step below 1 is not a value the original can produce, so it is
      // a corrupt file and the ctor's default stands.
      if (Number.isInteger(p.displayLevel) && (p.displayLevel as number) >= 1) {
        out.displayLevel = p.displayLevel as number
      }
      return out
    } catch {
      return {}
    }
  }

  /** `SoundService.ToggleSound` — flip, write back, `SaveLocalAsync`. */
  toggleSound(): boolean {
    this.data.isSoundEnabled = !this.data.isSoundEnabled
    this.save()
    return this.data.isSoundEnabled
  }

  /** `SoundService.ToggleHaptic`. */
  toggleHaptic(): boolean {
    this.data.isHapticEnabled = !this.data.isHapticEnabled
    this.save()
    return this.data.isHapticEnabled
  }

  /**
   * 🔍 `LevelService.OnLevelCompleted` — `__0x20d7f70.c`:25-31, RVA 0x20d7f70:
   *
   *     iVar1 = *(int*)(this + 0x10) + 1;   // _userLevel
   *     *(int*)(this + 0x10) = iVar1;
   *     *(int*)(_saveService->Data + 0x14) = iVar1;   // displayLevel
   *     SaveService__SaveLocalAsync();
   *
   * One step, taken the moment the win is announced, then written and saved.
   * This is the **only** place the win path advances the ladder:
   * `SuccessModel.LoadLNextLevel` (0x2141cdc) is `LevelService.LoadLevel`
   * inlined and merely reads `_userLevel` at `lVar5+0x10`, so Continue must not
   * step it a second time.
   *
   * `OnLevelFailed` (0x20d7f00:26-27) and `SaveProgress` (0x20d7f38:25-26)
   * write `displayLevel = _userLevel` **without** incrementing — a no-op in
   * value, only meaningful if the save file could be lost mid-run. Deliberately
   * not ported, rather than overlooked.
   */
  completeLevel(): number {
    this.data.displayLevel += 1
    this.save()
    return this.data.displayLevel
  }

  /**
   * 🔍 `LevelService.SkipToLevel` — `__0x20d838c.c`:23-31, RVA 0x20d838c:
   *
   *     if (param_3 < 2) param_3 = 1;
   *     *(int*)(this + 0x10) = param_3;
   *     *(int*)(_saveService->Data + 0x14) = param_3;
   *     SaveService__SaveLocalAsync();
   *
   * The original's only door onto a chosen level, and it does write the save.
   * The port's `?level=` is that door (see `main.ts`), so it lands here rather
   * than being kept as a second, competing notion of "where you are".
   */
  skipToLevel(level: number): number {
    this.data.displayLevel = level < 2 ? 1 : level
    this.save()
    return this.data.displayLevel
  }

  private save(): void {
    // Before the storage write, and outside its `try`: a device whose store is
    // full or disabled is exactly the one that most needs the app's copy, so
    // the cloud leg must not be skipped by the local leg's failure.
    this.onSaved?.(this.data)
    if (!this.storage) return
    try {
      this.storage.setItem(this.key, JSON.stringify(this.data))
    } catch {
      // A full or private-mode store loses the switch on reload; the original
      // swallows its own write failures the same way (`_isLocalSaving` gate).
    }
  }
}
